import { css } from 'styled-components';
import { mixin } from '../helpers/mixins';
import { isDefined } from '../helpers/defined';
import { ellipsisStyle } from '../tokens/ellipsis';
import { styleForBreakpoints } from '../helpers/media-style';
import { size } from '../style/size';
import { height } from '../style/height';
import { width } from '../style/width';
import { minWidth } from '../style/min-width';
import { maxWidth } from '../style/max-width';
import { minHeight } from '../style/min-height';
import { maxHeight } from '../style/max-height';
import { fullWidth } from '../style/full-width';
import { fullHeight } from '../style/full-height';
import { boxSizing } from '../style/box-sizing';
import { zIndex } from '../style/z-index';
import { padding } from '../style/padding';
import { paddingLeft } from '../style/padding-left';
import { paddingRight } from '../style/padding-right';
import { paddingBottom } from '../style/padding-bottom';
import { paddingTop } from '../style/padding-top';
import { paddingVertical } from '../style/padding-vertical';
import { paddingHorizontal } from '../style/padding-horizontal';
import { margin } from '../style/margin';
import { marginLeft } from '../style/margin-left';
import { marginRight } from '../style/margin-right';
import { marginBottom } from '../style/margin-bottom';
import { marginTop } from '../style/margin-top';
import { marginVertical } from '../style/margin-vertical';
import { marginHorizontal } from '../style/margin-horizontal';
export const fullHeightMixin = mixin(fullHeight, ['fullHeight', '$fullHeight']);
export const fullHeightKeys = ['fullHeight'];
export const fullWidthMixin = mixin(fullWidth, ['fullWidth', '$fullWidth']);
export const fullWidthKeys = ['fullWidth'];
export const ellipsisDisplayMixin = ({
  ellipsis = false,
  $ellipsis = false
}) => /* prettier-ignore */
ellipsis || $ellipsis ? `
    display: inline-block;
    ${ellipsisStyle}
  ` : '';
export const ellipsisMixin = ({
  ellipsis = false,
  $ellipsis = false
}) => ellipsis || $ellipsis ? ellipsisStyle : '';
export const ellipsisKeys = ['ellipsis'];
export const zIndexMixin = mixin(zIndex, ['zIndex', '$zIndex']);
export const zIndexKeys = ['zIndex'];
export const heightMixin = mixin(height, ['height', '$height', 'h', '$h']);
export const heightKeys = ['height', 'h'];
export const widthMixin = mixin(width, ['width', '$width', 'w', '$w']);
export const widthKeys = ['width', 'w'];
export const minHeightMixin = mixin(minHeight, ['minHeight', '$minHeight']);
export const minHeightKeys = ['minHeight'];
export const minWidthMixin = mixin(minWidth, ['minWidth', '$minWidth']);
export const minWidthKeys = ['minWidth'];
export const maxHeightMixin = mixin(maxHeight, ['maxHeight', '$maxHeight']);
export const maxHeightKeys = ['maxHeight'];
export const maxWidthMixin = mixin(maxWidth, ['maxWidth', '$maxWidth']);
export const maxWidthKeys = ['maxWidth'];
export const sizeMixin = css`
  ${mixin(size, ['size', '$size', 's', '$s'])}
  ${heightMixin}
  ${widthMixin}
  ${minHeightMixin}
  ${minWidthMixin}
  ${maxHeightMixin}
  ${maxWidthMixin}
`;
export const sizeKeys = ['size', 's', ...heightKeys, ...widthKeys, ...minHeightKeys, ...maxHeightKeys, ...minWidthKeys, ...maxWidthKeys];
export const marginVerticalMixin = css`
  ${({
  marginVertical: value,
  mv,
  my
}) => (isDefined(value) || isDefined(mv) || isDefined(my)) && marginVertical(value ?? mv ?? my)}
  ${({
  marginBottom: value,
  mb
}) => (isDefined(value) || isDefined(mb)) && marginBottom(value ?? mb)}
  ${({
  marginTop: value,
  mt
}) => (isDefined(value) || isDefined(mt)) && marginTop(value ?? mt)}
`;
export const paddingVerticalMixin = css`
  ${({
  paddingVertical: value,
  pv,
  py
}) => (isDefined(value) || isDefined(pv) || isDefined(py)) && paddingVertical(value ?? pv ?? py)}
  ${({
  paddingBottom: value,
  pb
}) => (isDefined(value) || isDefined(pb)) && paddingBottom(value ?? pb)}
  ${({
  paddingTop: value,
  pt
}) => (isDefined(value) || isDefined(pt)) && paddingTop(value ?? pt)}
`;
export const marginVerticalKeys = ['marginVertical', 'marginBottom', 'marginTop', 'mv', 'my', 'mb', 'mt'];
export const paddingVerticalKeys = ['paddingVertical', 'paddingBottom', 'paddingTop', 'pv', 'py', 'pb', 'pt'];
export const marginHorizontalMixin = css`
  ${({
  marginHorizontal: value,
  mh,
  mx
}) => (isDefined(value) || isDefined(mh) || isDefined(mx)) && marginHorizontal(value ?? mh ?? mx)}
  ${({
  marginLeft: value,
  ml
}) => (isDefined(value) || isDefined(ml)) && marginLeft(value ?? ml)}
  ${({
  marginRight: value,
  mr
}) => (isDefined(value) || isDefined(mr)) && marginRight(value ?? mr)}
`;
export const paddingHorizontalMixin = css`
  ${({
  paddingHorizontal: value,
  ph,
  px
}) => (isDefined(value) || isDefined(ph) || isDefined(px)) && paddingHorizontal(value ?? ph ?? px)}
  ${({
  paddingLeft: value,
  pl
}) => (isDefined(value) || isDefined(pl)) && paddingLeft(value ?? pl)}
  ${({
  paddingRight: value,
  pr
}) => (isDefined(value) || isDefined(pr)) && paddingRight(value ?? pr)}
`;
export const marginHorizontalKeys = ['marginHorizontal', 'marginLeft', 'marginRight', 'mh', 'mx', 'ml', 'mr'];
export const paddingHorizontalKeys = ['paddingHorizontal', 'paddingLeft', 'paddingRight', 'ph', 'px', 'pl', 'pr'];
export const marginMixin = css`
  ${marginVerticalMixin}
  ${marginHorizontalMixin}
  ${({
  margin: value,
  m
}) => (isDefined(value) || isDefined(m)) && margin(value ?? m)}
`;
export const paddingMixin = css`
  ${paddingVerticalMixin}
  ${paddingHorizontalMixin}
  ${({
  padding: value,
  p
}) => (isDefined(value) || isDefined(p)) && padding(value ?? p)}
`;
export const marginKeys = ['margin', 'm', ...marginHorizontalKeys, ...marginVerticalKeys];
export const paddingKeys = ['padding', 'p', ...paddingHorizontalKeys, ...paddingVerticalKeys];
export const relativeMixin = ({
  relative = false,
  $relative = false
}) => relative || $relative ? `position: relative;` : '';
export const relativeKeys = ['relative'];
export const $showMixin = ({
  $show = true
}) => styleForBreakpoints($show, $show => !$show ? `display: none;` : '');
export const $hideMixin = ({
  $hide = false
}) => styleForBreakpoints($hide, $hide => $hide ? `display: none;` : '');
export const showMixin = ({
  show = true
}) => styleForBreakpoints(show, show => !show ? `display: none;` : '');
export const hideMixin = ({
  hide = false
}) => styleForBreakpoints(hide, hide => hide ? `display: none;` : '');
export const boxSizingMixin = mixin(boxSizing, ['boxSizing', 'sizing'], 'border-box');
export const boxSizingKeys = ['boxSizing', 'sizing'];